import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, doc, deleteDoc, setDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword, sendPasswordResetEmail, signOut } from 'firebase/auth';
import { toast } from 'react-hot-toast';
import { db, auth, secondaryAuth } from '../config/firebase';
import { errorMessage, formatDate } from '../lib/format';

/** Contraseña que nadie conoce: la persona crea la suya con el enlace del correo. */
const randomPassword = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(24)), (b) => b.toString(16).padStart(2, '0')).join('');

/**
 * Un documento en super_admins solo da permiso: sin cuenta en Auth la persona
 * no tenía con qué entrar. Se crea la cuenta y, una vez dado el permiso, se le
 * envía el enlace para elegir contraseña; si ya tenía cuenta (p. ej. es dueña
 * de una tienda), entra con la contraseña de siempre.
 *
 * La cuenta va ANTES que el permiso: isSuperAdmin() no exige correo
 * verificado, así que un correo con permiso y sin cuenta sería de quien lo
 * registrara primero desde la app.
 */
async function ensureAccount(email) {
  const second = secondaryAuth();
  try {
    await createUserWithEmailAndPassword(second, email, randomPassword());
    await signOut(second);
    return 'created';
  } catch (error) {
    if (error.code === 'auth/email-already-in-use') return 'existing';
    console.error('Error creating admin account:', error);
    return 'failed';
  }
}

function Admins({ currentEmail }) {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(null);

  const me = (currentEmail || '').toLowerCase();

  const fetchAdmins = useCallback(async () => {
    try {
      const snapshot = await getDocs(collection(db, 'super_admins'));
      setAdmins(snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id)));
      setError(null);
    } catch (err) {
      console.error('Error fetching admins:', err);
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    // El id tiene que ser el correo en minúsculas: es lo que comparan las reglas.
    const email = newEmail.trim().toLowerCase();
    const name = newName.trim();
    if (!email || !name) return;
    if (admins.some((a) => a.id === email)) {
      toast.error('Ese correo ya es super admin.');
      return;
    }

    setSaving(true);
    const account = await ensureAccount(email);
    if (account === 'failed') {
      toast.error(`No se pudo crear la cuenta de ${email}, así que no se le dio permiso. Revisa el correo y vuelve a intentarlo.`, { duration: 8000 });
      setSaving(false);
      return;
    }

    try {
      await setDoc(doc(db, 'super_admins', email), { email, name, createdAt: Date.now(), createdBy: me });
    } catch (err) {
      console.error('Error adding admin:', err);
      toast.error(`No se pudo agregar. ${errorMessage(err)}`);
      setSaving(false);
      return;
    }

    if (account === 'created') {
      let sent = true;
      try {
        await sendPasswordResetEmail(secondaryAuth(), email);
      } catch (err) {
        console.error('Error sending password email:', err);
        sent = false;
      }
      toast.success(sent
        ? `${name} agregado. Le enviamos un correo a ${email} para que cree su contraseña.`
        : `${name} agregado. No se pudo enviar el correo: que use «¿Olvidaste tu contraseña?» al entrar.`, { duration: 6000 });
    } else {
      toast.success(`${name} agregado. Ya tenía cuenta: entra con su contraseña actual.`, { duration: 6000 });
    }

    setNewEmail('');
    setNewName('');
    setShowAddForm(false);
    setSaving(false);
    fetchAdmins();
  };

  const handleDelete = async (id) => {
    setConfirming(null);
    try {
      await deleteDoc(doc(db, 'super_admins', id));
      toast.success('Acceso al panel retirado.');
      fetchAdmins();
    } catch (err) {
      console.error('Error deleting admin:', err);
      toast.error(`No se pudo eliminar. ${errorMessage(err)}`);
    }
  };

  const handleResetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success(`Enlace para cambiar la contraseña enviado a ${email}.`);
    } catch (err) {
      console.error('Error sending reset email:', err);
      toast.error('No se pudo enviar el correo.');
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Super admins</h1>
          <p className="page-subtitle">Quién puede entrar a este panel y ver todas las tiendas</p>
        </div>
        <button onClick={() => setShowAddForm(!showAddForm)} className="btn btn-primary">
          {showAddForm ? 'Cancelar' : '+ Nuevo admin'}
        </button>
      </div>

      {showAddForm && (
        <div className="card section-gap-bottom">
          <div className="card-header"><h2 className="card-title">Agregar super admin</h2></div>
          <form onSubmit={handleAddAdmin} className="card-body inline-form">
            <label className="field">
              <span className="field-label">Nombre</span>
              <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} className="input-field" placeholder="Ej: Juan Pérez" required />
            </label>
            <label className="field">
              <span className="field-label">Correo electrónico</span>
              <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="input-field" placeholder="persona@empresa.com" required />
            </label>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <><span className="spinner" /> Guardando…</> : 'Agregar'}
            </button>
          </form>
          <p className="card-footnote">
            Si el correo no tiene cuenta, se crea y le llega un enlace para elegir su contraseña.
            Tendrá acceso completo a los datos de todas las tiendas.
          </p>
        </div>
      )}

      {error && <div className="notice notice-error">No se pudo cargar la lista. {error}</div>}

      <div className="card">
        <div className="table-container">
          {loading ? (
            <div className="empty-state"><span className="spinner spinner-dark" /></div>
          ) : admins.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">🛡️</div><p>No hay administradores registrados</p></div>
          ) : (
            <table>
              <thead><tr><th>Nombre</th><th>Correo</th><th>Agregado</th><th className="num">Acciones</th></tr></thead>
              <tbody>
                {admins.map((admin) => {
                  const isMe = admin.id === me;
                  return (
                    <tr key={admin.id}>
                      <td className="font-semibold">
                        {admin.name || '—'} {isMe && <span className="badge badge-neutral">Tú</span>}
                      </td>
                      <td>{admin.email || admin.id}</td>
                      <td className="text-secondary">{formatDate(admin.createdAt, false)}</td>
                      <td className="num">
                        {confirming === admin.id ? (
                          <div className="row-actions">
                            <span className="text-sm">¿Quitar acceso?</span>
                            <button onClick={() => handleDelete(admin.id)} className="btn btn-danger btn-sm">Sí, quitar</button>
                            <button onClick={() => setConfirming(null)} className="btn btn-ghost btn-sm">No</button>
                          </div>
                        ) : (
                          <div className="row-actions">
                            <button onClick={() => handleResetPassword(admin.email || admin.id)} className="btn btn-ghost btn-sm">
                              Enviar enlace de contraseña
                            </button>
                            <button
                              onClick={() => setConfirming(admin.id)}
                              className="btn btn-danger btn-sm"
                              disabled={isMe}
                              title={isMe ? 'No puedes quitarte el acceso a ti mismo' : undefined}
                            >
                              Quitar
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default Admins;
